import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DayPicker, type DateRange } from 'react-day-picker'
import { it as itLoc, enGB, de as deLoc } from 'date-fns/locale'
import { differenceInCalendarDays } from 'date-fns'
import { ArrowRight, Clock, MapPin, Tag, CalendarDays, ChevronDown } from 'lucide-react'
import { Header } from '../../components/layout/Header'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { MapView } from '../../components/ui/MapView'
import { TransportIcon } from '../../components/ui/TransportIcon'
import { LanguageSwitcher } from '../../components/layout/LanguageSwitcher'
import { getMainContext } from '../../lib/api/booking'
import { loadTransportTiles, saveTransportTiles, type TransportTile } from '../../lib/mainContextCache'
import { useAuth } from '../../store/authStore'
import { useBooking } from '../../store/bookingStore'
import { buildTimeSlots, hhmm, parseDay, toDay, prettyDayShort } from '../../lib/format'
import { stepPath } from '../../lib/steps'
import type { Locale, TransportContext } from '../../lib/api/types'
import { cn } from '../../lib/cn'

const DF = { it: itLoc, en: enGB, de: deLoc }

const WEBCAM_URL = import.meta.env.VITE_WEBCAM_IMAGE_URL as string | undefined
// Garage San Marco — Piazzale Roma, Venezia (free OpenStreetMap, no API key / no Google)
const MARKER = { lat: 45.4378, lon: 12.3186 }
const OSM_FULL = `https://www.openstreetmap.org/?mlat=${MARKER.lat}&mlon=${MARKER.lon}#map=17/${MARKER.lat}/${MARKER.lon}`

export function HomeScreen() {
  const { t } = useTranslation()
  const { locale: routeLocale = 'it' } = useParams()
  const navigate = useNavigate()
  const status = useAuth((s) => s.status)
  const goBook = () => navigate(stepPath(routeLocale, 'dates'))

  return (
    <div className="min-h-dvh bg-ink-50">
      <Header />

      {/* ── HERO (photo) + booking widget ── */}
      <BookingHero routeLocale={routeLocale} />

      <main className="mx-auto max-w-5xl px-5 pb-2 sm:px-6 xl:max-w-6xl">
        {/* ── how it works ── */}
        <section className="pt-16 lg:pt-20">
          <h2 className="mb-7 text-center font-display text-2xl font-extrabold tracking-tight text-ink-900 lg:text-3xl">{t('home.howTitle')}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6">
            {[
              { n: 1, t: t('home.step1Title'), b: t('home.step1Body') },
              { n: 2, t: t('home.step2Title'), b: t('home.step2Body') },
              { n: 3, t: t('home.step3Title'), b: t('home.step3Body') },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-white p-6 shadow-card ring-1 ring-ink-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 font-display text-sm font-extrabold text-white">
                  {s.n}
                </span>
                <h3 className="mt-4 font-display font-bold text-ink-900">{s.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── webcam (only when configured) ── */}
        {WEBCAM_URL && (
          <section className="pt-16">
            <div className="mb-3 flex items-center justify-center gap-2">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('home.webcamTitle')}</h2>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {t('home.webcamLive')}
              </span>
            </div>
            <div className="overflow-hidden rounded-2xl bg-ink-900 ring-1 ring-ink-100">
              <img src={WEBCAM_URL} alt="" loading="lazy" className="aspect-video w-full object-cover" />
            </div>
          </section>
        )}

        {/* ── conventions ── */}
        <section className="pt-16">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-7 text-white sm:p-9">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold">
              <Tag className="h-3.5 w-3.5" /> 10%
            </span>
            <h2 className="mt-4 max-w-lg font-display text-2xl font-extrabold tracking-tight">{t('home.convTitle')}</h2>
            <p className="mt-2 max-w-xl text-white/80">{t('home.convBody')}</p>
            <Button variant="outline" className="mt-5 border-white/30 bg-white/10 text-white hover:bg-white/20" onClick={goBook}>
              {t('home.convCta')}
            </Button>
          </div>
        </section>

        {/* ── location + real (OpenStreetMap) map ── */}
        <section className="pt-16">
          <h2 className="mb-5 text-center font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('home.whereTitle')}</h2>
          <div className="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-ink-100">
            <MapView lat={MARKER.lat} lon={MARKER.lon} className="aspect-[16/11] w-full sm:aspect-[2/1]" />
            <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 space-y-2.5 text-sm">
                <p className="flex items-start gap-2.5 text-ink-700">
                  <MapPin className="mt-0.5 h-4 w-4 flex-none text-brand-600" /> {t('home.address')}
                </p>
                <p className="flex items-center gap-2.5 text-ink-700">
                  <Clock className="h-4 w-4 flex-none text-brand-600" /> {t('home.hours')}
                </p>
                <p className="pl-[26px] text-ink-500">{t('home.directionsNote')}</p>
              </div>
              <a href={OSM_FULL} target="_blank" rel="noreferrer" className="flex-none">
                <Button variant="outline" leftIcon={<MapPin className="h-4 w-4" />}>{t('home.directions')}</Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ── footer ── */}
      <footer className="mt-16 bg-ink-900 text-white">
        <div className="mx-auto max-w-5xl px-4 py-14 text-center sm:px-6 xl:max-w-6xl">
          <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{t('home.footerTitle')}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-white/70">{t('home.footerSub')}</p>
          <div className="mx-auto mt-6 max-w-xs">
            <Button size="lg" className="w-full" rightIcon={<ArrowRight className="h-5 w-5" />} onClick={goBook}>
              {t('home.book')}
            </Button>
          </div>
          <div className="mt-9 flex flex-col items-center gap-4 border-t border-white/10 pt-7">
            <LanguageSwitcher />
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/70">
              <Link to={`/${routeLocale}/faq`} className="hover:text-white">{t('nav.faq')}</Link>
              <Link to={`/${routeLocale}/contacts`} className="hover:text-white">{t('nav.contacts')}</Link>
              {status === 'authenticated' ? (
                <>
                  <Link to={`/${routeLocale}/bookings`} className="hover:text-white">{t('nav.myBookings')}</Link>
                  <Link to={`/${routeLocale}/profile`} className="hover:text-white">{t('nav.profile')}</Link>
                </>
              ) : (
                <Link to={`/${routeLocale}/login`} className="hover:text-white">{t('nav.login')}</Link>
              )}
            </div>
            <p className="text-xs text-white/40">{t('home.smallprint')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

/* ── photo hero with an inline quick-book widget (vehicle + in-app calendar + time → funnel) ── */
function BookingHero({ routeLocale }: { routeLocale: string }) {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const navigate = useNavigate()
  const setContexts = useBooking((s) => s.setContexts)
  const setTransport = useBooking((s) => s.setTransport)
  const setDates = useBooking((s) => s.setDates)

  const { data } = useQuery({ queryKey: ['mainContext'], queryFn: getMainContext, staleTime: 60_000 })
  useEffect(() => {
    if (data) {
      setContexts(data)
      saveTransportTiles(data) // remember the vehicle tiles so the next visit paints icons instantly
    }
  }, [data, setContexts])
  // Vehicle tiles: real data once loaded, else last-cached / static defaults — so the icons render
  // immediately instead of shimmer while /api/configuratorMainContext is still in flight.
  const tiles: TransportTile[] = useMemo(
    () => (data ? data.map((c) => ({ id: c.transport.id, label: c.transport.label, iconUri: c.transport.iconUri })) : loadTransportTiles()),
    [data],
  )

  const [transportId, setTransportId] = useState<number | null>(null)
  const [range, setRange] = useState<DateRange | undefined>()
  const [time, setTime] = useState('')
  const [calOpen, setCalOpen] = useState(false)
  // true once the user has begun a fresh range (entry picked, waiting for the exit click)
  const picking = useRef(false)

  useEffect(() => {
    if (data?.length && transportId == null) setTransportId(data[0].transport.id)
  }, [data, transportId])
  const ctx: TransportContext | undefined = useMemo(() => data?.find((c) => c.transport.id === transportId), [data, transportId])
  useEffect(() => {
    if (ctx) {
      setRange((r) => r ?? { from: parseDay(ctx.minDate), to: parseDay(ctx.defaultEndDate) })
      setTime((t2) => t2 || ctx.minTime)
    }
  }, [ctx])

  // calendar opens as an overlay popover — close it when clicking outside.
  const calRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!calOpen) return
    picking.current = false // each time it opens, the first click starts a fresh range
    function onDoc(e: MouseEvent) {
      if (calRef.current && !calRef.current.contains(e.target as Node)) setCalOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [calOpen])

  const from = range?.from ? toDay(range.from) : ''
  const to = range?.to ? toDay(range.to) : ''
  const timeSlots = useMemo(() => {
    if (!ctx) return []
    const onMinDay = from === ctx.minDate
    return buildTimeSlots(ctx.bookingHoursSubdivision, onMinDay ? ctx.minTime : '00:00:00')
  }, [ctx, from])

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0
  const overMax = ctx ? nights > ctx.bookingMaxDays : false
  const canBook = Boolean(transportId && from && to && time && !overMax)
  const rangeLabel = range?.from
    ? `${prettyDayShort(toDay(range.from), locale)} → ${range.to ? prettyDayShort(toDay(range.to), locale) : '…'}`
    : t('dates.selectRange')

  function book() {
    if (transportId && from) {
      setTransport(transportId)
      if (from && to && time) setDates(from, time, to)
    }
    navigate(canBook ? stepPath(routeLocale, 'rate') : stepPath(routeLocale, 'dates'))
  }

  return (
    <section className="relative bg-ink-900 text-white">
      {/* hero photo */}
      <img
        src="/hero.jpg"
        alt=""
        {...{ fetchpriority: 'high' }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* brand gradient overlay (kept) — tints the photo toward the Venetian palette */}
      <div className="absolute inset-0 opacity-75" style={{ background: 'linear-gradient(160deg,#1b2a4a 0%,#3a4a6b 38%,#9a6b4a 78%,#d9a05b 100%)' }} />
      {/* dark layer for text contrast */}
      <div className="absolute inset-0 bg-ink-950/40" />
      <div className="relative mx-auto max-w-5xl px-5 py-12 sm:px-6 sm:py-16 lg:py-24 xl:max-w-6xl xl:py-28">
        {/* desktop: split hero (copy left, booking card right); mobile: stacked (grid off) */}
        {/* items-start so the left copy stays top-aligned when the right card grows (calendar open) */}
        <div className="lg:grid lg:grid-cols-[1fr_1.1fr] lg:items-start lg:gap-12 xl:gap-16">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
              <MapPin className="h-3.5 w-3.5" /> {t('home.eyebrow')}
            </span>
            <h1 className="mt-5 max-w-[15ch] font-display text-[34px] font-extrabold leading-[1.02] tracking-tight drop-shadow sm:text-6xl lg:text-[58px] xl:text-7xl">
              {t('home.heroTitle')}
            </h1>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-white/85 drop-shadow sm:text-lg lg:mt-5 lg:text-xl">
              {t('home.heroSubtitle')}
            </p>
            {/* desktop-only trust row (reuses existing copy) */}
            <div className="mt-8 hidden flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-white/90 lg:flex">
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-white/70" /> {t('home.hours')}
              </span>
            </div>
          </div>

        {/* quick-book widget */}
        <div className="mt-7 max-w-xl rounded-2xl bg-white p-4 text-ink-900 shadow-card-hover sm:p-5 lg:mt-0 lg:w-full lg:max-w-lg lg:justify-self-end lg:p-6 lg:shadow-pop">
          {/* vehicle (icon) — tiles paint instantly from cached/static icons; they become
              selectable once the live context loads, so the booking data streams in after. */}
          <div className="grid grid-cols-2 gap-2">
            {tiles.map((tr) => {
              const active = Boolean(data) && transportId === tr.id
              return (
                <button
                  key={tr.id}
                  type="button"
                  disabled={!data}
                  onClick={() => setTransportId(tr.id)}
                  aria-label={tr.label}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl border-2 py-2.5 transition lg:gap-2 lg:py-4',
                    active ? 'border-brand-600 bg-brand-50' : 'border-ink-100 hover:border-ink-200',
                  )}
                >
                  <span className="flex h-8 w-14 items-center justify-center lg:h-12 lg:w-24">
                    <TransportIcon iconUri={tr.iconUri} label={tr.label} className="h-8 w-auto lg:h-12" />
                  </span>
                  <span className={cn('text-xs font-semibold lg:text-sm', active ? 'text-brand-700' : 'text-ink-500')}>{tr.label}</span>
                </button>
              )
            })}
          </div>

          {/* dates — compact summary that opens the calendar as an OVERLAY popover
              (absolute → floats above the content, never grows the card height) */}
          <div className="relative mt-3" ref={calRef}>
            <button type="button" onClick={() => setCalOpen((o) => !o)} className="gsm-input flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2 font-medium">
                <CalendarDays className="h-4 w-4 flex-none text-brand-600" />
                <span className="truncate capitalize">{rangeLabel}</span>
              </span>
              <ChevronDown className={cn('h-4 w-4 flex-none text-ink-400 transition-transform', calOpen && 'rotate-180')} />
            </button>
            {calOpen && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-ink-100 bg-white p-2 shadow-pop">
                {ctx ? (
                  <>
                    <DayPicker
                      className="w-full"
                      mode="range"
                      locale={DF[locale]}
                      numberOfMonths={1}
                      weekStartsOn={1}
                      selected={range}
                      onSelect={(r, triggerDate) => {
                        if (!picking.current) {
                          // first click = ENTRY date; ignore the pre-existing range and wait for the exit
                          setRange(triggerDate ? { from: triggerDate, to: undefined } : r)
                          picking.current = Boolean(triggerDate)
                          return
                        }
                        // second click = EXIT date → only now is the range complete, so close
                        setRange(r)
                        if (r?.from && r?.to) {
                          picking.current = false
                          setCalOpen(false)
                        } else {
                          picking.current = Boolean(r?.from)
                        }
                      }}
                      startMonth={parseDay(ctx.minDate)}
                      disabled={[{ before: parseDay(ctx.minDate) }, { after: parseDay(ctx.maxDate) }]}
                    />
                    <div className="mt-1 flex items-center justify-between gap-2 border-t border-ink-100 px-1 pt-2">
                      <span className="min-w-0 truncate text-xs font-medium capitalize text-ink-500">
                        {range?.from
                          ? `${prettyDayShort(toDay(range.from), locale)} → ${range.to ? prettyDayShort(toDay(range.to), locale) : '…'}`
                          : t('dates.selectRange')}
                      </span>
                      <button
                        type="button"
                        disabled={!range?.from}
                        onClick={() => {
                          picking.current = false
                          // confirm: a lone entry date becomes a same-day stay, then close
                          setRange((r) => (r?.from && !r.to ? { from: r.from, to: r.from } : r))
                          setCalOpen(false)
                        }}
                        className="flex-none rounded-lg bg-brand-600 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-brand-700 disabled:bg-ink-200 disabled:text-ink-400"
                      >
                        {t('summary.confirm')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="skeleton h-64 w-full rounded-lg" />
                )}
              </div>
            )}
          </div>

          {/* time (opens upward so it never drops under the next section) */}
          <div className="mt-3">
            <span className="mb-1 block text-[12px] font-medium text-ink-500">{t('dates.time')}</span>
            <Select
              ariaLabel={t('dates.time')}
              value={time}
              options={timeSlots.map((s) => ({ value: s, label: hhmm(s) }))}
              onChange={(v) => {
                setTime(v)
                setCalOpen(false) // collapse the calendar once a time slot is chosen
              }}
              icon={<Clock className="h-4 w-4 text-brand-600" />}
              openUp
              disabled={!ctx}
            />
            {overMax && ctx && (
              <p className="mt-1.5 text-xs font-medium text-brand-600">
                {nights} {t('common.days')} · max {ctx.bookingMaxDays}
              </p>
            )}
          </div>

          <button
            onClick={book}
            disabled={!canBook}
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-base font-bold text-white transition hover:bg-brand-700 disabled:bg-brand-300"
          >
            {t('home.book')} <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-2 text-center text-xs text-ink-400">{t('home.rateTeaser')}</p>
        </div>
        </div>
      </div>
    </section>
  )
}
